import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { take } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { User } from 'app/core/shared/types/user.type';
import { UserTypeEnum } from 'app/core/user/user.enum';
import { UserService } from 'app/core/user/user.service';
import { unknownNavigation, subNavigation } from '360kit/data-store/navigation-menu.data';
import { systemNavigation } from '360kit/data-store/navigation-type-system.data';
import { adminNavigation } from '360kit/data-store/navigation-type-admin.data';
import { userNavigation } from '360kit/data-store/navigation-type-user.data';
import { superNavigation } from '360kit/data-store/navigation-type-super.data';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
  private readonly _superNavigation: FuseNavigationItem[] = superNavigation;
  private readonly _systemNavigation: FuseNavigationItem[] = systemNavigation;
  private readonly _adminNavigation: FuseNavigationItem[] = adminNavigation;
  private readonly _userNavigation: FuseNavigationItem[] = userNavigation;
  private readonly _unknownNavigation: FuseNavigationItem[] = unknownNavigation;
  private readonly _subNavigation: FuseNavigationItem[] = subNavigation;

  private _itemNavigation: FuseNavigationItem[] = [];

  /**
   * Constructor
   */
  constructor(
    private _fuseMockApiService: FuseMockApiService,
    private _userService: UserService
  ) {
    // Register Mock API handlers
    this.registerHandlers();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register Mock API handlers
   */
  registerHandlers(): void {
    // -----------------------------------------------------------------------------------------------------
    // @ Navigation - GET
    // -----------------------------------------------------------------------------------------------------
    this._fuseMockApiService
      .onGet('api/common/navigation')
      .reply(() => {
        this._userService.user$
          .pipe(take(1))
          .subscribe((user: User) => {
            if (user.userType === UserTypeEnum.SUPER) {
              this._itemNavigation = this._superNavigation;
            } else if (user.userType === UserTypeEnum.SYSTEM) {
              this._itemNavigation = this._systemNavigation;
            } else if (user.userType === UserTypeEnum.ADMIN) {
              this._itemNavigation = this._adminNavigation;
            } else if (user.userType === UserTypeEnum.USER) {
              this._itemNavigation = this._userNavigation;
            } else {
              this._itemNavigation = this._unknownNavigation;
            }

            this._itemNavigation.forEach((horizontalNavItem) => {
              this._subNavigation.forEach((defaultNavItem) => {
                if (defaultNavItem.id === horizontalNavItem.id) {
                  horizontalNavItem.children = cloneDeep(defaultNavItem.children);

                  if (defaultNavItem.id === 'space-route') {
                    this._userService.spaceRouteClone.next(cloneDeep(defaultNavItem.children));
                  }
                }
              });
            });
            
            // Filter Route Menu based on User Roles
            this._itemNavigation = this.filterRouteNavigationBasedOnRouteItem(this._itemNavigation, user);
          });

        // Return the response
        return [
          200,
          {
            default: cloneDeep(this._itemNavigation),
            horizontal: cloneDeep(this._itemNavigation)
          },
        ];
      });
  }

  filterRouteNavigationBasedOnRouteItem(navigationItem: FuseNavigationItem[], userData: User): FuseNavigationItem[] {
    if (userData.userType === UserTypeEnum.SUPER || userData.userType === UserTypeEnum.ADMIN) {
      return navigationItem;
    } else if (userData.userType === UserTypeEnum.SYSTEM || userData.userType === UserTypeEnum.USER) {
      const filteredNavigation: FuseNavigationItem[] = [];

      for (const item of navigationItem) {
        const filteredChildren: FuseNavigationItem[] = [];

        if (item.children) {
          for (const child of item.children) {
            if (child.accessRole) {
              if (child.accessRole.some(role => userData.roles.includes(role))) {
                filteredChildren.push(child);
              }
            } else {
              filteredChildren.push(child);
            }
          }
        }

        if (filteredChildren.length > 0) {
          const filteredGroup: FuseNavigationItem = { ...item, children: filteredChildren };
          filteredNavigation.push(filteredGroup);
        }
      }
      return filteredNavigation;
    }
    return [];
  }

  filterRouteNavigationBasedOnRouteGroup(navigationItem: FuseNavigationItem[], userData: User): FuseNavigationItem[] {  
    return navigationItem.filter((item) => {
      if (userData.userType === UserTypeEnum.SUPER || userData.userType === UserTypeEnum.ADMIN) {
        return true;
      }
  
      if (userData.userType === UserTypeEnum.SYSTEM || userData.userType === UserTypeEnum.USER) {
        if (item.accessRole && userData.roles) {
          return item.accessRole.some((allowedRole) => userData.roles.includes(allowedRole));
        } else {
          return true;
        }
      }
  
      return false;
    });
  }
}
